import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import NotificationBar from "../../components/public/NotificationBar";

function Home() {
  const [settings, setSettings] = useState(null);
  const [sliders, setSliders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [aboutBlock, setAboutBlock] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, slidersRes, notifRes, contentRes, facilitiesRes] =
        await Promise.all([
          axiosInstance.get("/api/settings"),
          axiosInstance.get("/api/sliders"),
          axiosInstance.get("/api/notifications"),
          axiosInstance.get("/api/content"),
          axiosInstance.get("/api/facilities"),
        ]);
      setSettings(settingsRes.data);
      setSliders(slidersRes.data);
      setNotifications(notifRes.data);
      setAboutBlock(contentRes.data.find((b) => b.block_key === "about"));
      setFacilities(facilitiesRes.data.slice(0, 6));
    } catch (err) {
      console.error("Failed to load homepage data", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  const primaryColor = settings?.theme_primary_color || "#0d6efd";
  const logoUrl = settings?.logo_path
    ? `${process.env.REACT_APP_API_URL}/${settings.logo_path}`
    : null;

  return (
    <div>
      {/* Hero — real carousel if slides exist, static banner fallback if not */}
      {sliders.length > 0 ? (
        <div
          id="heroCarousel"
          className="carousel slide"
          data-bs-ride="carousel"
        >
          <div className="carousel-indicators">
            {sliders.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                data-bs-target="#heroCarousel"
                data-bs-slide-to={index}
                className={index === 0 ? "active" : ""}
                aria-current={index === 0 ? "true" : undefined}
                aria-label={`Slide ${index + 1}`}
              ></button>
            ))}
          </div>

          <div className="carousel-inner">
            {sliders.map((slide, index) => {
              const imageUrl = `${process.env.REACT_APP_API_URL}/${slide.image_path}`;
              const slideContent = (
                <>
                  <img
                    src={imageUrl}
                    className="d-block w-100"
                    alt={slide.title || "Slide"}
                    style={{ height: "460px", objectFit: "cover" }}
                  />
                  {(slide.title || slide.caption) && (
                    <div
                      className="carousel-caption d-none d-md-block text-start"
                      style={{
                        background: "rgba(0,0,0,0.45)",
                        borderRadius: "8px",
                        left: "5%",
                        right: "auto",
                        maxWidth: "500px",
                        bottom: "15%",
                      }}
                    >
                      {slide.title && (
                        <h2 className="fw-bold">{slide.title}</h2>
                      )}
                      {slide.caption && <p className="mb-0">{slide.caption}</p>}
                    </div>
                  )}
                </>
              );

              return (
                <div
                  className={`carousel-item ${index === 0 ? "active" : ""}`}
                  key={slide.id}
                >
                  {slide.link_url ? (
                    <a href={slide.link_url}>{slideContent}</a>
                  ) : (
                    slideContent
                  )}
                </div>
              );
            })}
          </div>

          {sliders.length > 1 && (
            <>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target="#heroCarousel"
                data-bs-slide="prev"
              >
                <span className="carousel-control-prev-icon"></span>
              </button>
              <button
                className="carousel-control-next"
                type="button"
                data-bs-target="#heroCarousel"
                data-bs-slide="next"
              >
                <span className="carousel-control-next-icon"></span>
              </button>
            </>
          )}
        </div>
      ) : (
        <section
          className="text-white py-5"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, #212529)`,
          }}
        >
          <div className="container py-4 text-center">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={settings?.school_name}
                style={{ height: "90px" }}
                className="mb-3"
              />
            )}
            <h1 className="fw-bold">{settings?.school_name}</h1>
            <p className="lead mb-1">{settings?.trust_name}</p>
            {settings?.tagline && (
              <p className="fs-5 opacity-75">{settings.tagline}</p>
            )}
            <div className="mt-4">
              <Link to="/contact" className="btn btn-light btn-lg px-4">
                Enquire Now <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Notifications ticker */}
      {notifications.length > 0 && (
        <NotificationBar
          notifications={notifications}
          primaryColor={primaryColor}
        />
      )}

      {/* About preview */}
      {aboutBlock && (
        <section className="py-5">
          <div className="container">
            <div className="row align-items-center g-4">
              <div className="col-12 col-md-6">
                <h2 className="fw-bold mb-3">
                  {aboutBlock.title || "About Us"}
                </h2>
                <p className="text-muted" style={{ whiteSpace: "pre-line" }}>
                  {aboutBlock.body?.length > 300
                    ? aboutBlock.body.slice(0, 300) + "..."
                    : aboutBlock.body}
                </p>
                <Link to="/about" className="btn btn-outline-primary">
                  Read More
                </Link>
              </div>
              <div className="col-12 col-md-6 text-center">
                <i
                  className="bi bi-mortarboard"
                  style={{
                    fontSize: "10rem",
                    color: primaryColor,
                    opacity: 0.15,
                  }}
                ></i>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Facilities preview */}
      {facilities.length > 0 && (
        <section className="py-5 bg-light">
          <div className="container">
            <h2 className="fw-bold text-center mb-5">Our Facilities</h2>
            <div className="row g-4">
              {facilities.map((f) => (
                <div className="col-12 col-sm-6 col-lg-4" key={f.id}>
                  <div className="card border-0 shadow-sm h-100 text-center">
                    <div className="card-body p-4">
                      {f.icon_path ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${f.icon_path}`}
                          alt={f.title}
                          className="rounded-circle mb-3"
                          style={{
                            width: "64px",
                            height: "64px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <i
                          className="bi bi-building mb-3 d-block"
                          style={{ fontSize: "2.5rem", color: primaryColor }}
                        ></i>
                      )}
                      <h6>{f.title}</h6>
                      <p className="text-muted small mb-0">{f.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-4">
              <Link to="/facilities" className="btn btn-outline-primary">
                View All Facilities
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section
        className="py-5 text-white text-center"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="container">
          <h3 className="fw-bold mb-2">
            Ready to join {settings?.school_name}?
          </h3>
          <p className="mb-4 opacity-75">
            Get in touch with us for admissions and enquiries.
          </p>
          <Link to="/contact" className="btn btn-light btn-lg px-4">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
